// ─────────────────────────────────────────────────────────────────
// src/services/advisorService.ts
//
// INTEGRATION POINT — When ready to connect an LLM:
//
// 1. Set ADVISOR_API_URL to your LLM endpoint
// 2. Replace the body of sendMessage() with your API call
// 3. Map the LLM response to AdvisorResponse
//
// Everything else (UI, state, history) works unchanged.
// ─────────────────────────────────────────────────────────────────

export type AdvisorMessage = {
    id: string;
    role: "user" | "assistant" | "system";
    content: string;
    timestamp: Date;
    metadata?: {
      sources?: string[];
      confidence?: number;
      action?: AdvisorAction;
    };
  };
  
  export type AdvisorAction = {
    type: "navigate" | "open_form" | "show_product" | "none";
    target?: string; // route path or form source key
    label?: string;
  };
  
  export type AdvisorResponse = {
    content: string;
    metadata?: AdvisorMessage["metadata"];
  };
  
  export type AdvisorConfig = {
    apiUrl?: string;
    apiKey?: string;
    model?: string;
    systemPrompt?: string;
    temperature?: number;
    maxTokens?: number;
  };
  
  // ─── Default config (override via setAdvisorConfig) ─────────────
  let config: AdvisorConfig = {
    apiUrl: "", // e.g. "https://api.openai.com/v1/chat/completions"
    apiKey: "",
    model: "gpt-4",
    temperature: 0.3,
    maxTokens: 1000,
    systemPrompt: `You are Everpesa's financial advisor assistant operating in Uganda.
  You help users understand:
  - Unit trust funds (UAP Old Mutual, Stanbic, ICEA Lion, Xeno, Sanlam)
  - P2P lending investment opportunities on the Everpesa platform
  - Loan products (business cash loans, device financing)
  - Wallet operations (deposits, withdrawals, transfers)
  - Risk assessment and portfolio allocation
  
  Currency is UGX. Be concise, practical, and specific to the Ugandan market.
  Never give specific buy/sell recommendations — provide education and guidance.
  When relevant, suggest the user navigate to a specific section of the app.`,
  };
  
  export const setAdvisorConfig = (newConfig: Partial<AdvisorConfig>) => {
    config = { ...config, ...newConfig };
  };
  
  export const getAdvisorConfig = () => ({ ...config });
  
  // ─── MAIN INTEGRATION POINT ─────────────────────────────────────
  //
  // Replace this function body with your LLM API call.
  // The signature stays the same — the hook and UI don't change.
  //
  export const sendMessage = async (
    message: string,
    history: AdvisorMessage[]
  ): Promise<AdvisorResponse> => {
    // ┌─────────────────────────────────────────────────────────┐
    // │  WHEN READY TO INTEGRATE:                                │
    // │                                                          │
    // │  const messages = [                                      │
    // │    { role: "system", content: config.systemPrompt },     │
    // │    ...history.map(m => ({                                │
    // │      role: m.role,                                       │
    // │      content: m.content                                  │
    // │    })),                                                  │
    // │    { role: "user", content: message }                    │
    // │  ];                                                      │
    // │                                                          │
    // │  const res = await fetch(config.apiUrl!, {                │
    // │    method: "POST",                                       │
    // │    headers: {                                             │
    // │      "Content-Type": "application/json",                 │
    // │      "Authorization": `Bearer ${config.apiKey}`,         │
    // │    },                                                    │
    // │    body: JSON.stringify({                                 │
    // │      model: config.model,                                │
    // │      messages,                                           │
    // │      temperature: config.temperature,                    │
    // │      max_tokens: config.maxTokens,                       │
    // │    }),                                                   │
    // │  });                                                     │
    // │                                                          │
    // │  const data = await res.json();                          │
    // │  return {                                                │
    // │    content: data.choices[0].message.content,             │
    // │  };                                                      │
    // └─────────────────────────────────────────────────────────┘
  
    // Simulated delay
    await new Promise((r) => setTimeout(r, 800 + Math.random() * 700));
  
    // Offline responses for demo/testing
    return getOfflineResponse(message);
  };
  
  // ─── Offline response engine (remove when LLM is connected) ─────
  function getOfflineResponse(message: string): AdvisorResponse {
    const q = message.toLowerCase();
  
    if (q.includes("hello") || q.includes("hi") || q.includes("hey")) {
      return {
        content:
          "Hello! I'm your Everpesa financial advisor. I can help you understand investment opportunities, loan products, and manage your finances. What would you like to know?",
      };
    }
  
    if (q.includes("unit trust") || q.includes("fund")) {
      return {
        content:
          "Uganda has several licensed unit trust providers:\n\n• **UAP Old Mutual** — Money Market (11.5%), Balanced (14%), Equity (18%)\n• **Stanbic** — Bond Fund (13.6%), Money Market (10.5%)\n• **ICEA Lion** — Money Market (11.3%), Equity (18%)\n• **Xeno** — Money Market (12%), Growth (16%)\n• **Sanlam** — Income Fund (13.1%), Balanced (14.5%)\n\nAll are licensed by the Capital Markets Authority. Would you like details on any specific fund?",
        metadata: {
          action: {
            type: "navigate",
            target: "/list/institutions",
            label: "Browse Marketplace",
          },
        },
      };
    }
  
    if (q.includes("invest") || q.includes("opportunity") || q.includes("earn")) {
      return {
        content:
          "On Everpesa, you can invest in two ways:\n\n1. **P2P Lending** — Fund vetted borrower loans and earn interest (rates vary by loan duration and risk tier)\n2. **Unit Trusts** — Invest through licensed fund managers like UAP Old Mutual, Stanbic, and others\n\nP2P investments start from UGX 1,000 per unit. Unit trusts start as low as UGX 10,000 with Xeno.\n\nWhich interests you more?",
        metadata: {
          action: {
            type: "navigate",
            target: "/invest",
            label: "Browse Investments",
          },
        },
      };
    }
  
    if (q.includes("loan") || q.includes("borrow")) {
      return {
        content:
          "Everpesa offers two loan products:\n\n1. **Business Cash Loans** — For working capital, expansion, or personal needs. Durations from 1 week to 3 months. Interest rates from 10-20% depending on duration.\n\n2. **Device Financing (BNPL)** — Purchase phones and devices on installment plans of 3, 6, or 12 months with a deposit.\n\nBoth require KYC verification. Would you like to apply?",
        metadata: {
          action: {
            type: "navigate",
            target: "/loans",
            label: "View Loans",
          },
        },
      };
    }
  
    if (q.includes("risk") || q.includes("safe") || q.includes("secure")) {
      return {
        content:
          "Here's a general risk guide for the Ugandan market:\n\n🟢 **Low Risk** — Money Market Funds, Treasury Bills, Fixed Deposits (8-12% p.a.)\n🟡 **Medium Risk** — Balanced Funds, Income Funds, P2P Lending (12-16% p.a.)\n🔴 **Higher Risk** — Equity Funds, Growth Portfolios (14-22% p.a.)\n\nAs a rule: never invest money you might need within 6 months in equity funds. For emergency savings, Money Market Funds offer the best balance of access and returns.\n\nWould you like me to help assess your risk profile?",
      };
    }
  
    if (q.includes("wallet") || q.includes("deposit") || q.includes("withdraw") || q.includes("transfer")) {
      return {
        content:
          "Your Everpesa wallet supports:\n\n• **Top Up** — Deposit from mobile money (MTN, Airtel)\n• **Withdraw** — Send to your mobile money number\n• **Transfer** — Send to another Everpesa wallet\n\nAll deposits are available instantly. Withdrawals typically process within 1-2 minutes. The minimum transaction is UGX 500.",
        metadata: {
          action: {
            type: "navigate",
            target: "/wallet",
            label: "Go to Wallet",
          },
        },
      };
    }
  
    if (q.includes("kyc") || q.includes("verify") || q.includes("document")) {
      return {
        content:
          "To access loans and investments, you need KYC verification:\n\n1. Complete your profile (name, phone, date of birth)\n2. Provide your National ID, Passport, or Driver's License\n3. Set your investment preferences\n\nAn admin will review your documents — this usually takes 1-2 business days. Once approved, all financial features unlock.",
        metadata: {
          action: {
            type: "navigate",
            target: "/onboard",
            label: "Complete KYC",
          },
        },
      };
    }
  
    if (q.includes("fee") || q.includes("charge") || q.includes("cost")) {
      return {
        content:
          "Everpesa fees:\n\n• **Platform fee** — 20% of interest earned on P2P investments\n• **Management fee** — 2% of invested principal\n• **Mobile money** — Standard telecom charges apply for deposits/withdrawals\n• **Wallet transfers** — Free between Everpesa wallets\n• **Unit trusts** — Each fund has its own fee structure (typically 1-2% annual management fee)\n\nThere are no account opening or maintenance fees.",
      };
    }
  
    // Default
    return {
      content:
        "That's a great question. I can help with:\n\n• **Investments** — P2P lending and unit trust opportunities\n• **Loans** — Business cash loans and device financing\n• **Wallet** — Deposits, withdrawals, and transfers\n• **Risk** — Understanding your risk profile\n• **KYC** — Account verification process\n• **Fees** — Platform charges and costs\n\nWhat would you like to explore?",
    };
  }